import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "@/i18n/translations";
import { adminTranslations } from "@/i18n/adminTranslations";
import { translationsDe } from "@/i18n/translations.de";
import { translationsFr } from "@/i18n/translations.fr";
import { translationsEs } from "@/i18n/translations.es";

const STORAGE_KEY = "stoicos_lang";
const DEFAULT_LANG = "en";
const SUPPORTED = ["en", "nl", "de", "fr", "es"];
const EXTRA = { de: translationsDe, fr: translationsFr, es: translationsEs };

// Merge admin dictionary into every locale. Keys are namespaced under
// `admin.*` so they never collide with existing public-site keys.
// Missing keys in any locale fall back to English inside t().
const mergedTranslations = SUPPORTED.reduce((acc, code) => {
  acc[code] = { ...(translations[code] || EXTRA[code] || {}), ...(adminTranslations[code] || {}) };
  return acc;
}, {});

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang && SUPPORTED.includes(urlLang)) return urlLang;
    } catch (_) {}
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
    } catch (_) {}
    return DEFAULT_LANG;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) {}
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  const dict = mergedTranslations[lang] || mergedTranslations[DEFAULT_LANG];

  const t = useCallback(
    (key, vars) => {
      let raw = dict[key];
      if (raw == null) raw = mergedTranslations[DEFAULT_LANG][key] ?? key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          raw = raw.split(`{${k}}`).join(String(vars[k]));
        }
      }
      return raw;
    },
    [dict]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  return useContext(LanguageContext);
}
